import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Download, FileText, Film, Music, X } from 'lucide-react'
import { formatBytes, type Attachment } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { useCamadaDeSobreposicao } from '@/components/ui'

/**
 * Prévia de anexo.
 *
 * Imagens abrem em tela cheia ao clicar; vídeo e áudio tocam no lugar; o resto
 * vira um cartão com nome, tamanho e link de download. O `max-w` evita que uma
 * captura de tela em 4K empurre o layout do chat.
 */
export function AttachmentPreview({ attachment }: { attachment: Attachment }) {
  const [expanded, setExpanded] = useState(false)
  const camada = useCamadaDeSobreposicao()

  const isImage = attachment.contentType.startsWith('image/')
  const isVideo = attachment.contentType.startsWith('video/')
  const isAudio = attachment.contentType.startsWith('audio/')

  if (isImage) {
    // Reserva o espaço pela proporção real, para a lista não pular quando a
    // imagem termina de carregar.
    const ratio =
      attachment.width && attachment.height ? attachment.width / attachment.height : 16 / 9

    return (
      <>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="block max-w-md overflow-hidden rounded-lg transition-opacity hover:opacity-90"
          style={{ aspectRatio: ratio, maxHeight: 320 }}
        >
          <img
            src={attachment.url}
            alt={attachment.filename}
            loading="lazy"
            className="size-full object-cover"
          />
        </button>

        {expanded
          ? createPortal(
              <div
                className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 p-8 animate-fade-in"
                onClick={() => setExpanded(false)}
              >
                <img
                  src={attachment.url}
                  alt={attachment.filename}
                  className="max-h-full max-w-full rounded-lg object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  aria-label="Fechar"
                  className="absolute top-6 right-6 rounded-full bg-surface-overlay p-2 text-content-primary transition-colors hover:bg-surface-hover"
                >
                  <X className="size-5" />
                </button>
                <a
                  href={attachment.url}
                  download={attachment.filename}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-6 bottom-6 flex items-center gap-2 rounded-lg bg-surface-overlay px-3 py-2 text-sm text-content-primary transition-colors hover:bg-surface-hover"
                >
                  <Download className="size-4" />
                  Baixar
                </a>
              </div>,
              camada,
            )
          : null}
      </>
    )
  }

  if (isVideo) {
    return (
      <video
        controls
        preload="metadata"
        src={attachment.url}
        className="max-h-80 max-w-md rounded-lg bg-black"
      />
    )
  }

  if (isAudio) {
    return (
      <div className="flex w-80 max-w-full items-center gap-3 rounded-lg bg-surface-raised p-3">
        <Music className="size-8 shrink-0 text-content-muted" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium text-content-primary">
            {attachment.filename}
          </p>
          <audio controls src={attachment.url} className="mt-1.5 h-8 w-full" />
        </div>
      </div>
    )
  }

  const Icon = attachment.contentType.includes('pdf') ? FileText : Film

  return (
    <a
      href={attachment.url}
      download={attachment.filename}
      className={cn(
        'flex w-72 items-center gap-3 rounded-lg bg-surface-raised p-3',
        'transition-colors hover:bg-surface-overlay',
      )}
    >
      <Icon className="size-8 shrink-0 text-brand" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-brand">{attachment.filename}</p>
        <p className="text-[11px] text-content-muted">{formatBytes(attachment.size)}</p>
      </div>
      <Download className="size-4 shrink-0 text-content-muted" />
    </a>
  )
}
